import { TRYON_SERVER_URL } from "../config";

const RESOLUTIONS = ["1K", "2K", "4K"];

const creditsForImage = (resolution = "2K", count = 1) => {
    const n = Math.min(4, Math.max(1, Number(count) || 1));
    const each = String(resolution).toUpperCase() === "4K" ? 48 : 27;
    return each * n;
};

/**
 * Generate or edit an image. The server picks the model.
 * Reference files are sent as `image` (one) or `images` (several).
 *
 * @param {Object} options
 * @param {string} options.prompt
 * @param {string} [options.resolution]
 * @param {number} [options.n]
 * @param {File[]} [options.images]
 * @param {string} [options.accessToken]
 */
const generateImage = async ({
    prompt,
    resolution = "2K",
    n = 1,
    images = [],
    accessToken = null,
}) => {
    try {
        if (!prompt || !String(prompt).trim()) {
            return { data: null, status: "error", statusCode: 400, message: "Enter a prompt" };
        }

        const size = RESOLUTIONS.includes(resolution) ? resolution : "2K";
        const count = Math.min(4, Math.max(1, Number(n) || 1));
        const files = (images || []).filter((file) => file instanceof File);

        const formData = new FormData();
        formData.append("prompt", String(prompt).trim());
        formData.append("resolution", size);
        formData.append("n", String(count));
        if (files.length === 1) {
            formData.append("image", files[0]);
        } else {
            files.forEach((file) => formData.append("images", file));
        }

        const headers = {};
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(`${TRYON_SERVER_URL}/api/v1/image/generate/`, {
            method: "POST",
            headers,
            body: formData,
        });

        const payload = await response.json().catch(() => null);

        if (response.ok) {
            return {
                data: payload,
                status: "success",
                statusCode: response.status,
                message: "Image generated",
            };
        }

        return {
            data: null,
            status: "failure",
            statusCode: response.status,
            message: payload?.message || payload?.error || "Failed to generate image",
        };
    } catch (error) {
        console.error("Error generating image:", error);
        return {
            data: null,
            status: "failure",
            statusCode: 500,
            message: "Network error: " + error.message,
        };
    }
};

/**
 * The download route requires the bearer token, so load it as a blob URL.
 */
const loadGeneratedImage = async (downloadPath, accessToken) => {
    if (!downloadPath) {
        throw new Error("Image download URL is missing");
    }
    const url = downloadPath.startsWith("http")
        ? downloadPath
        : `${TRYON_SERVER_URL}${downloadPath.startsWith("/") ? "" : "/"}${downloadPath}`;
    const headers = {};
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
        throw new Error("Could not load the generated image");
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
};

export { RESOLUTIONS, creditsForImage, generateImage, loadGeneratedImage };
